import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Import axios configuration (sets up interceptors)
import './utils/axiosConfig';

// Shared components
import Header from './components/shared/Header';
import Footer from './components/shared/Footer';
import ProtectedRoute from './components/shared/ProtectedRoute';

// Public pages
import HomePage from './components/public/HomePage';
import LoginPage from './components/public/LoginPage';
import RegisterPage from './components/public/RegisterPage';
import ForgotPasswordPage from './components/public/ForgotPasswordPage';
import ResetPasswordPage from './components/public/ResetPasswordPage';
import VerifyEmailPage from './components/auth/VerifyEmailPage';

// Dashboard components
import AdminDashboard from './components/admin/AdminDashboard';
import PlayerDashboard from './components/player/PlayerDashboard';

// CSS
import './App.css';

// Component to handle password reset token redirects
function TokenHandler() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      // Redirect to reset-password page with token
      navigate(`/reset-password?token=${token}`, { replace: true });
    }
  }, [searchParams, navigate]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen bg-gray-50">
          <Header />

          <main className="container mx-auto px-3 sm:px-4 md:px-6 py-6 sm:py-8 max-w-7xl">
            <TokenHandler />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email/:token" element={<VerifyEmailPage />} />

              {/* Protected player route */}
              <Route
                path="/player"
                element={
                  <ProtectedRoute>
                    <PlayerDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Protected admin route */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Catch-all redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          <Footer />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
